import {
  CommonModule,
  isPlatformBrowser,
  NgOptimizedImage,
} from '@angular/common';
import {
  Component,
  computed,
  inject,
  model,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { onAuthStateChanged, Unsubscribe } from 'firebase/auth';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { PopoverModule } from 'primeng/popover';
import { from } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResponsiveService } from '../../core/api/v1/responsive/responsive.service';
import { UserDto } from '../../core/api/v1/user/user.model';
import { UserService } from '../../core/api/v1/user/user.service';
import { AuthService } from '../../core/auth/auth.service';
import {
  buildPmsUrl,
  PMS_SIGN_IN_PATH,
} from '../../shared/utils/pms-link.util';
import { LanguagesComponent } from '../languages/languages.component';

/**
 * Voce di navigazione: interna (routerLink) oppure esterna (href, nuova scheda).
 * `external` discrimina l'unione così il template può fare narrowing col @if.
 */
type NavigationItem =
  | {
      id: string;
      title: string;
      visible: boolean;
      external?: false;
      link: string;
    }
  | {
      id: string;
      title: string;
      visible: boolean;
      external: true;
      href: string;
    };

@Component({
  selector: 'app-header',
  imports: [
    ButtonModule,
    CommonModule,
    DialogModule,
    DividerModule,
    NgOptimizedImage,
    NgClickOutsideDirective,
    RouterLink,
    TranslocoPipe,
    PopoverModule,
    LanguagesComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly responsiveService = inject(ResponsiveService);
  protected readonly translocoService = inject(TranslocoService);

  screenSize = toSignal(this.responsiveService.screenSize, {
    initialValue: 'default',
  });

  loggedInUser = signal<UserDto | null>(null);

  // Signal che "reagisce" al cambio lingua
  private languageSignal = toSignal(this.translocoService.langChanges$);

  navigationItems = computed<NavigationItem[]>(() => {
    // Ricalcola tutto questo array se la lingua cambia.
    this.languageSignal();

    return [
      {
        id: 'add-your-property',
        title: 'navigation.add_your_property.title',
        link: '/join-us',
        visible: true,
      },
      {
        id: 'manager-area',
        title: 'navigation.manager_area.title',
        external: true,
        href: buildPmsUrl(PMS_SIGN_IN_PATH, 'header_btn', 'header'),
        visible: true,
      },
      {
        id: 'download-app',
        title: 'navigation.download_app.title',
        link: '/download-app',
        visible: true,
      },
    ];
  });

  authItems = computed(() => {
    // Ricalcola tutto questo array se la lingua cambia.
    this.languageSignal();

    return [
      {
        id: 'profile',
        title: 'navigation.profile.title',
        link: '/account',
        visible: !!this.loggedInUser(),
      },
      {
        id: 'reservations',
        title: 'navigation.bookings.title',
        link: '/reservations',
        visible: !!this.loggedInUser(),
      },
      {
        id: 'reviews',
        title: 'navigation.reviews.title',
        link: '/reviews',
        visible: false,
      },
      {
        id: 'sign-out',
        title: 'navigation.sign_out.title',
        command: this.signOut.bind(this),
        visible: !!this.loggedInUser(),
      },
      {
        id: 'sign-in',
        title: 'navigation.sign_in.title',
        link: '/sign-in',
        visible: !this.loggedInUser(),
      },
    ];
  });

  showMenu = model<boolean>(false);

  unsubscribeFromAuthState: Unsubscribe | undefined;

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.getLoggedInUser();
    }
  }

  ngOnDestroy() {
    if (this.unsubscribeFromAuthState) {
      this.unsubscribeFromAuthState(); // Unsubscribe from auth state changes
    }
  }

  private getLoggedInUser() {
    from(this.authService.auth().authStateReady()).subscribe(() => {
      if (this.authService.auth().currentUser) {
        this.userService.getCurrentUser().subscribe({
          next: (user) => {
            this.loggedInUser.set(user);
          },
          error: (error) => {
            console.error('Error fetching user data', error);
            this.loggedInUser.set(null);
          },
        });
      } else {
        this.loggedInUser.set(null);
      }
    });

    this.unsubscribeFromAuthState = onAuthStateChanged(
      this.authService.auth(),
      (user) => {
        if (user) {
          this.userService.getCurrentUser().subscribe({
            next: (userData) => {
              this.loggedInUser.set(userData);
            },
            error: (error) => {
              console.error('Error fetching user data', error);
              this.loggedInUser.set(null);
            },
          });
        } else {
          this.loggedInUser.set(null);
        }
      },
    );
  }

  signIn() {
    this.router.navigate(['/sign-in'], {
      queryParams: { redirectURL: this.router.url },
    });
  }

  signOut() {
    this.authService.signOut().subscribe({
      next: () => {
        this.router.navigate(['/']);
        this.loggedInUser.set(null);
        this.showMenu.set(false);
      },
      error: (error) => {
        console.error('Error signing out', error);
      },
    });
  }

  onDownloadApp() {
    if (this.screenSize() === 'default') {
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.open(
          navigator.language === 'it-IT'
            ? environment.downloadLinks.ios.it
            : environment.downloadLinks.ios.us,
          '_blank',
        );
        return;
      }

      window.open(
        navigator.language === 'it-IT'
          ? environment.downloadLinks.android.it
          : environment.downloadLinks.android.us,
        '_blank',
      );
      return;
    }

    this.router.navigate(['/download-app']);
  }
}
